'use client';

import { useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { faMagnifyingGlass, faTimes } from '@fortawesome/pro-light-svg-icons';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { PatientSelectionModalKeys } from '@/app/(main)/_components/patient-selection/_types/patient-selection-modal-keys.enum';
import { formatAgeRange } from '@/app/(main)/_components/patient-selection/_utils/patient-age-range.util';
import { Button } from '@/components/ui/button/button.component';
import { Icon } from '@/components/ui/icon.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { useProgressiveModal } from '@/components/ui/progressive-modal/progressive-modal.hook';
import { formatDocumentNumber } from '@/utils/formatters/format-document-number';
import { buildOutOfAgeRangeMessage } from '@/utils/patient/build-out-of-age-range-message.util';
import { getCartOrigin } from '@/utils/cart/get-cart-origin.util';
import { CartOrigin } from '@/types/enums/analytics/cart/cart-analytics.enum';
import { PatientOption, ScheduleAgeRange } from '@/types/patient-selection.types';

export interface PatientSelectionListProps {
  patients: PatientOption[];
  ageRange: ScheduleAgeRange;
  onSelectPatient: (patient: PatientOption) => void;
}

const firstNameOf = ({ name }: PatientOption): string => name.split(' ')[0];

export function PatientSelectionList({ patients, ageRange, onSelectPatient }: PatientSelectionListProps) {
  const { handleJump, onClose } = useProgressiveModal<PatientSelectionModalKeys>();
  const pathname = usePathname();

  const canSearchAnother = getCartOrigin(pathname) === CartOrigin.SEARCH_RESULTS;

  const outOfRangeNames = useMemo(
    () => patients.filter((patient) => !patient.isWithinAgeRange).map(firstNameOf),
    [patients],
  );

  const warningMessage = buildOutOfAgeRangeMessage(outOfRangeNames, formatAgeRange(ageRange));

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-start justify-between gap-2">
        <Typography
          variant={TypographyVariant.MODAL_TITLE}
          data-testid={DataTestIdEnum.PATIENT_SELECTION_LIST_TITLE}
        >
          Escolha o paciente
        </Typography>
        <button
          type="button"
          aria-label="Fechar modal"
          data-testid={DataTestIdEnum.PATIENT_SELECTION_LIST_CLOSE_BUTTON}
          className="shrink-0 flex size-8 items-center justify-center cursor-pointer"
          onClick={onClose}
        >
          <Icon icon={faTimes} className="text-gray-500 hover:text-gray-900 text-2xl" />
        </button>
      </div>

      <Typography
        variant={TypographyVariant.BODY_TEXT}
        className="text-gray-700"
        data-testid={DataTestIdEnum.PATIENT_SELECTION_LIST_SUBTITLE}
      >
        O agendamento deve ser feito em nome de quem será atendido.
      </Typography>

      {warningMessage && (
        <div
          className="flex w-full items-center gap-3 rounded-2xl border border-warning-300 bg-warning-100 py-3 pl-3 pr-4 shadow-floating"
          data-testid={DataTestIdEnum.PATIENT_SELECTION_AGE_WARNING}
        >
          <Icon icon={faCircleExclamation} className="shrink-0 text-xl text-warning-600" />
          <p className="text-base font-semibold text-warning-700">{warningMessage}</p>
        </div>
      )}

      <div className="flex flex-col gap-4">
        {patients.map((patient, index) => (
          <Button
            key={`${patient.id}-${patient.documentNumber}`}
            disabled={!patient.isWithinAgeRange}
            variant="secondary"
            color="primary"
            className="flex flex-col gap-1 py-3"
            data-testid={`${DataTestIdEnum.PATIENT_SELECTION_OPTION}-${patient.type}-${index}`}
            onClick={() => onSelectPatient(patient)}
          >
            <Typography variant={TypographyVariant.BODY_TEXT} className="font-semibold text-inherit text-lg">
              {patient.name}
            </Typography>
            <Typography variant={TypographyVariant.BODY_TEXT} className="text-inherit text-lg">
              {formatDocumentNumber(patient.documentNumber)}
            </Typography>
          </Button>
        ))}
      </div>

      <Button
        variant="outline"
        color="primary"
        data-testid={DataTestIdEnum.PATIENT_SELECTION_CREATE_BUTTON}
        onClick={() => handleJump({ key: PatientSelectionModalKeys.CREATE })}
      >
        Agendar para outra pessoa
      </Button>

      {canSearchAnother && (
        <Button
          variant="text"
          color="primary"
          startIcon={faMagnifyingGlass}
          startIconSize="1x"
          data-testid={DataTestIdEnum.PATIENT_SELECTION_SEARCH_ANOTHER_BUTTON}
          onClick={onClose}
        >
          Procurar outro médico
        </Button>
      )}
    </div>
  );
}
